import { Pencil, Trash2, Send, EyeOff, FileEdit, Download, PlusCircle, History, ChevronDown, ChevronUp } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import API from '../services/api';

function Flows() {
  const [flows, setFlows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);
  const [isHistoryExpanded, setIsHistoryExpanded] = useState(false); // ✅ Default minimized
  const navigate = useNavigate();
  const isSuperAdmin = localStorage.getItem('isSuperAdmin') === 'true';
  const hasSubscription = localStorage.getItem('subscriptionStatus') === 'active';
  const subscriptionPlan = localStorage.getItem('subscriptionPlan') || 'Custom Flow Setup';
  const subscriptionPlanId = localStorage.getItem('subscriptionPlanId') || '';
  const isFullSetupPlan = 
    subscriptionPlan.includes('Full Setup') || 
    subscriptionPlan.includes('Credits') ||
    subscriptionPlanId === '2' || 
    subscriptionPlanId === '4';
  const location = useLocation();
  const [showFlowSetupModal, setShowFlowSetupModal] = useState(false);
  const [flowSetupRequests, setFlowSetupRequests] = useState([]);
  const [submittingRequest, setSubmittingRequest] = useState(false);
  const [requirements, setRequirements] = useState('');

  const handleCreateFlow = () => {
    if (!isSuperAdmin && !hasSubscription) {
      navigate('/pricing');
      return;
    }
    navigate('/flow-builder');
  };

  useEffect(() => {
    loadFlows();
    if (hasSubscription && isFullSetupPlan) {
      loadFlowSetupRequests();
    }
  }, []);

  const loadFlows = async () => {
    try {
      setLoading(true);
      const response = await API.get('/flows');
      setFlows(response.data.data);
    } catch (error) {
      toast.error('Failed to load flows');
      console.error('Error loading flows:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadFlowSetupRequests = async () => {
    try {
      const res = await API.get('/flows/setup-request/my');
      if (res.data.hasRequest) {
        const list = Array.isArray(res.data.data) ? res.data.data : [res.data.data];
        setFlowSetupRequests(list);
      } else {
        setFlowSetupRequests([]);
        setShowFlowSetupModal(true);
      }
    } catch (err) {
      console.error('setup request error:', err);
    }
  };

  const hasActiveRequest = flowSetupRequests.some(
    r => r.status === 'pending' || r.status === 'in_progress'
  );

  const handleSubmitFlowRequest = async () => {
    if (!requirements.trim()) {
      toast.error('Please describe your business requirements.');
      return;
    }
    setSubmittingRequest(true);
    try {
      await API.post('/flows/setup-request', {
        planName: subscriptionPlan,
        businessRequirements: requirements
      });
      toast.success('Your request has been submitted! Our team will contact you shortly.');
      setShowFlowSetupModal(false);
      setRequirements('');
      await loadFlowSetupRequests();
    } catch (error) {
      if (error.response?.data?.errorCode === 'REQUEST_ALREADY_EXISTS') {
        toast.error('You already have an active request. Please wait for our team to respond.');
      } else {
        toast.error(error.response?.data?.message || 'Failed to submit request.');
      }
    }
    setSubmittingRequest(false);
  };

  const handleDelete = async (id, name) => {
    if (window.confirm(`Delete "${name}"? This action cannot be undone.`)) {
      const toastId = toast.loading('Deleting flow...');
      try {
        await API.delete(`/flows/${id}`);
        toast.success('Flow deleted successfully!', { id: toastId });
        loadFlows();
      } catch (error) {
        toast.error('Failed to delete flow', { id: toastId });
        console.error('Error deleting flow:', error);
      }
    }
  };

  const handlePublishToggle = async (id, currentStatus, name) => {
    const toastId = toast.loading(`${currentStatus ? 'Unpublishing' : 'Publishing'} flow...`);
    try {
      await API.patch(`/flows/${id}/publish`, { isPublished: !currentStatus });
      toast.success(`Flow ${currentStatus ? 'unpublished' : 'published'} successfully!`, { id: toastId });
      loadFlows();
    } catch (error) {
      if (error.response?.data?.errorCode === 'NO_ACTIVE_SUBSCRIPTION') {
        toast.error('Active subscription required to publish flows. Redirecting to plans...', { id: toastId, duration: 3000 });
        setTimeout(() => window.location.href = '/pricing', 2000);
      } else {
        toast.error(error.response?.data?.message || 'Failed to update flow status', { id: toastId });
      }
      console.error('Error updating flow:', error);
    }
  };

  const handleDeleteFlowRequest = async (requestId) => {
    if (!window.confirm('Are you sure you want to delete this flow setup request?')) return;
    try {
      await API.delete(`/flows/setup-request/${requestId}`);
      toast.success('Flow setup request deleted successfully.');
      await loadFlowSetupRequests();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete request.');
    }
  };

  const handleDownloadFlowFile = async (fileUrl, reqId) => {
    if (!fileUrl) return;

    if (fileUrl.startsWith('http://') || fileUrl.startsWith('https://')) {
      window.open(fileUrl, '_blank');
      return;
    }

    const backendBase = process.env.REACT_APP_API_URL
      ? process.env.REACT_APP_API_URL.replace(/\/api\/?$/, '')
      : 'http://localhost:5000';

    const fullUrl = `${backendBase}${fileUrl.startsWith('/') ? '' : '/'}${fileUrl}`;

    setDownloadingId(reqId);
    const toastId = toast.loading('Downloading your flow file...');
    try {
      const res = await fetch(fullUrl);
      if (!res.ok) throw new Error('File download failed');

      const blob = await res.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;

      const filename = fileUrl.split('/').pop() || 'flow-setup-file.json';
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);

      toast.success('Flow file downloaded successfully!', { id: toastId });
    } catch (err) {
      console.error('Download error:', err);
      toast.error('Failed to download flow file.', { id: toastId });
    } finally {
      setDownloadingId(null);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-content">
          <div className="loading-spinner">⏳</div>
          <p className="loading-text">Loading flows...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dash-page">
      {/* Flow Setup Requests List / History (Minimizable) */}
      {flowSetupRequests.length > 0 && (
        <div style={{
          background: '#ffffff',
          border: '1.5px solid #e2e8f0',
          borderRadius: '16px',
          padding: isHistoryExpanded ? '18px 20px' : '12px 18px',
          marginBottom: '24px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
          transition: 'all 0.2s ease'
        }}>
          {/* Header Bar */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '10px'
          }}>
            <div
              onClick={() => setIsHistoryExpanded(!isHistoryExpanded)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                cursor: 'pointer',
                userSelect: 'none'
              }}
            >
              <History size={18} color="#2563eb" />
              <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#1e293b' }}>
                Flow Setup Requests & Saved Files ({flowSetupRequests.length})
              </h3>
              <span style={{
                fontSize: '11px',
                background: '#eff6ff',
                color: '#2563eb',
                padding: '2px 8px',
                borderRadius: '12px',
                fontWeight: '600'
              }}>
                {flowSetupRequests.filter(r => r.status === 'completed').length} Ready
              </span>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {!hasActiveRequest && hasSubscription && isFullSetupPlan && (
                <button
                  onClick={() => {
                    setRequirements('');
                    setShowFlowSetupModal(true);
                  }}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '6px 12px',
                    background: '#166534',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '12px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  <PlusCircle size={14} /> Request Another Flow Setup
                </button>
              )}

              {/* Minimize / Maximize Toggle Button */}
              <button
                type="button"
                onClick={() => setIsHistoryExpanded(!isHistoryExpanded)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '6px 12px',
                  background: '#f8fafc',
                  border: '1px solid #cbd5e1',
                  borderRadius: '8px',
                  color: '#475569',
                  fontSize: '12px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                {isHistoryExpanded ? (
                  <>
                    <ChevronUp size={15} /> Minimize
                  </>
                ) : (
                  <>
                    <ChevronDown size={15} /> View Saved Files ({flowSetupRequests.length})
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Collapsible Cards Section */}
          {isHistoryExpanded && (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
              marginTop: '16px',
              borderTop: '1px solid #f1f5f9',
              paddingTop: '14px'
            }}>
              {flowSetupRequests.map((req) => (
                <div
                  key={req.id}
                  style={{
                    background: req.status === 'completed' ? '#f0fdf4' :
                                req.status === 'rejected'  ? '#fef2f2' : '#f8fafc',
                    border: `1px solid ${
                      req.status === 'completed' ? '#bbf7d0' :
                      req.status === 'rejected'  ? '#fecaca' : '#e2e8f0'}`,
                    borderRadius: '12px',
                    padding: '14px 16px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '8px' }}>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '16px' }}>
                          {req.status === 'completed' ? '✅' :
                           req.status === 'rejected'  ? '❌' :
                           req.status === 'in_progress' ? '🔄' : '⏳'}
                        </span>
                        <strong style={{ fontSize: '13px', color: '#1e293b' }}>
                          {req.status === 'pending'     ? 'Pending Review' :
                           req.status === 'in_progress' ? 'In Progress' :
                           req.status === 'completed'   ? 'Completed' : 'Rejected'}
                        </strong>
                      </div>
                      <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>
                        Submitted on {new Date(req.created_at).toLocaleString('en-IN', {
                          day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
                        })}
                      </div>
                    </div>

                    <button
                      onClick={() => handleDeleteFlowRequest(req.id)}
                      style={{
                        background: 'white',
                        border: '1px solid #fca5a5',
                        borderRadius: '6px',
                        padding: '3px 8px',
                        color: '#dc2626',
                        fontSize: '11px',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                    >
                      🗑️ Delete
                    </button>
                  </div>

                  <div style={{ fontSize: '12px', color: '#334155', background: 'rgba(255,255,255,0.7)', padding: '7px 10px', borderRadius: '6px' }}>
                    <strong>Requirement:</strong> {req.business_requirements}
                  </div>

                  {req.admin_notes && (
                    <div style={{ fontSize: '12px', color: '#1e293b', background: '#fef3c7', border: '1px solid #fde68a', padding: '7px 10px', borderRadius: '6px' }}>
                      <strong>Admin Note:</strong> {req.admin_notes}
                    </div>
                  )}

                  {req.flow_file_url && (
                    <div style={{ marginTop: '2px' }}>
                      <button
                        type="button"
                        onClick={() => handleDownloadFlowFile(req.flow_file_url, req.id)}
                        disabled={downloadingId === req.id}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '6px 12px',
                          background: downloadingId === req.id ? '#93c5fd' : '#2563eb',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '12px',
                          fontWeight: '600',
                          cursor: downloadingId === req.id ? 'not-allowed' : 'pointer'
                        }}
                      >
                        <Download size={13} />
                        {downloadingId === req.id ? 'Downloading...' : 'Download Flow File'}
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Flow Setup Request Modal */}
      {showFlowSetupModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
          backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center',
          justifyContent: 'center', zIndex: 99999, padding: '20px'
        }}>
          <div style={{
            background: 'white', borderRadius: '20px', maxWidth: '520px',
            width: '100%', overflow: 'hidden',
            boxShadow: '0 25px 50px rgba(0,0,0,0.2)'
          }}>
            <div style={{
              background: 'linear-gradient(135deg,#1e40af,#3b82f6)',
              padding: '24px 28px', color: 'white', position: 'relative'
            }}>
              <button
                onClick={() => { setShowFlowSetupModal(false); setRequirements(''); }}
                style={{
                  position: 'absolute', top: '14px', right: '14px',
                  background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)',
                  borderRadius: '8px', color: 'white', width: '28px', height: '28px',
                  cursor: 'pointer', fontSize: '14px', display: 'flex',
                  alignItems: 'center', justifyContent: 'center'
                }}
              >✕</button>
              <div style={{ fontSize: '28px', marginBottom: '8px' }}>🛠️</div>
              <h2 style={{ margin: '0 0 6px', fontSize: '20px', fontWeight: '800' }}>
                Request Flow Setup from Our Team
              </h2>
              <p style={{ margin: 0, opacity: 0.85, fontSize: '13px' }}>
                Describe your requirements and our team will build your custom flow
              </p>
            </div>

            <div style={{ padding: '24px 28px' }}>
              <div style={{
                background: '#f0f9ff', border: '1px solid #bae6fd',
                borderRadius: '12px', padding: '14px 16px', marginBottom: '20px'
              }}>
                <p style={{ margin: '0 0 6px', fontWeight: '700', fontSize: '14px', color: '#0c4a6e' }}>
                  How it works
                </p>
                <p style={{ margin: 0, fontSize: '13px', color: '#0369a1', lineHeight: '1.7' }}>
                  Since you selected the <strong>Full Setup by Us</strong> plan, our team will design
                  and build a custom WhatsApp flow tailored to your business. Simply describe your
                  requirements below, and we'll send you a ready-to-import flow file.
                </p>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', fontWeight: '700', fontSize: '13px', color: '#374151', marginBottom: '8px' }}>
                  Describe Your Business Requirements <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <textarea
                  value={requirements}
                  onChange={(e) => setRequirements(e.target.value)}
                  placeholder="Example: I run an online clothing store. I need a flow that greets customers, shows product categories, lets them place orders, and sends order confirmation messages..."
                  rows={5}
                  style={{
                    width: '100%', borderRadius: '10px', border: '1.5px solid #d1d5db',
                    padding: '12px', fontSize: '13px', lineHeight: '1.6',
                    resize: 'vertical', outline: 'none', boxSizing: 'border-box',
                    fontFamily: 'inherit', color: '#1e293b'
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  onClick={() => { setShowFlowSetupModal(false); setRequirements(''); }}
                  style={{
                    flex: 1, padding: '12px', borderRadius: '10px',
                    border: '1.5px solid #e2e8f0', background: 'white',
                    fontSize: '14px', fontWeight: '600', cursor: 'pointer', color: '#64748b'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmitFlowRequest}
                  disabled={submittingRequest || !requirements.trim()}
                  style={{
                    flex: 2, padding: '12px', borderRadius: '10px',
                    background: (submittingRequest || !requirements.trim()) ? '#94a3b8' : 'linear-gradient(135deg,#2563eb,#3b82f6)',
                    color: 'white', border: 'none', fontSize: '14px',
                    fontWeight: '700', cursor: (submittingRequest || !requirements.trim()) ? 'not-allowed' : 'pointer'
                  }}
                >
                  {submittingRequest ? '⏳ Submitting...' : '📩 Submit Request'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="dash-header">
        <div>
          <h1 className="dash-title">🔄 Flows</h1>
          <p className="dash-subtitle">Create and manage your chatbot conversation flows</p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          {hasSubscription && isFullSetupPlan && !hasActiveRequest && (
            <button
              onClick={() => {
                setRequirements('');
                setShowFlowSetupModal(true);
              }}
              className="flow-create-btn"
              style={{ background: '#059669', borderColor: '#059669' }}
            >
              <PlusCircle size={16} /> Request Setup
            </button>
          )}

          <button onClick={handleCreateFlow} className="flow-create-btn">
            <span className="flow-create-btn-icon">+</span>
            Create New Flow
          </button>
        </div>
      </div>

      {/* Flows Grid */}
      {flows.length > 0 ? (
        <div className="flows-grid">
          {flows.map(flow => (
            <div key={flow.id} className="flow-card">
              <div className="flow-card-header">
                <div className="flow-card-content">
                  <h3 className="flow-card-title">{flow.name}</h3>
                  <p className="flow-card-description">{flow.description || 'No description'}</p>
                </div>
                <div className={flow.is_published ? 'flow-status-badge-published' : 'flow-status-badge-draft'}>
                  {flow.is_published 
                    ? <><Send size={12} /> Published</> 
                    : <><FileEdit size={12} /> Draft</>}
                </div>
              </div>

              <div className="flow-stats">
                <div className="flow-stat-item">
                  <div className="flow-stat-label">Created</div>
                  <div className="flow-stat-value">{new Date(flow.created_at).toLocaleDateString()}</div>
                </div>
                <div className="flow-stat-item">
                  <div className="flow-stat-label">Updated</div>
                  <div className="flow-stat-value">{new Date(flow.updated_at).toLocaleDateString()}</div>
                </div>
              </div>

              <div className="flow-actions">
                <button
                  onClick={(e) => { e.stopPropagation(); navigate(`/flow-builder/${flow.id}`); }}
                  className="flow-btn-edit"
                >
                  <Pencil size={15} /> Edit
                </button>

                <button
                  onClick={(e) => { e.stopPropagation(); handlePublishToggle(flow.id, flow.is_published, flow.name); }}
                  className={flow.is_published ? 'flow-btn-unpublish' : 'flow-btn-publish'}
                >
                  {flow.is_published 
                    ? <><EyeOff size={15} /> Unpublish</> 
                    : <><Send size={15} /> Publish</>}
                </button>

                <button
                  onClick={(e) => { e.stopPropagation(); handleDelete(flow.id, flow.name); }}
                  className="flow-btn-delete"
                >
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="flows-empty-state">
          <div className="flows-empty-icon">🤖</div>
          <h3 className="flows-empty-title">No Flows Yet</h3>
          <p className="flows-empty-description">Create your first chatbot flow to get started</p>
          <button onClick={handleCreateFlow} className="flow-create-btn">
            + Create Your First Flow
          </button>
        </div>
      )}
    </div>
  );
}

export default Flows;